import React, { useEffect, useState } from 'react';
import { Form, Row, Col } from "react-bootstrap";
import { Upload } from 'lucide-react';
import Card from '../../UI/Card/Card';
import Select from '../../UI/Select/Select';
import Loader from '../../UI/loader/loader';
import userService from '../../app/services/user.service';

const UploadForm = ({
  fileType,
  setFileType,
  selectedTarget,
  setSelectedTarget,
  onStartIngestion,
  loading
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [questionFile, setQuestionFile] = useState(null);
  const [answerFile, setAnswerFile] = useState(null);
  const [solutionFile, setSolutionFile] = useState(null);
  const [targets, setTargets] = useState([]);


  useEffect(() => {
    const fetchTargets = async () => {
      try {
        const res = await userService.TargetList();
        console.log("Targets",res.target_data);
        setTargets(res.target_data); // store in state
      } catch (err) {
        console.error("Error fetching targets:", err);
      }
    };

    fetchTargets();
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadedFile(e.target.files[0]);
    }
  };

  const handleQuestionFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setQuestionFile(e.target.files[0]);
    }
  };

  const handleAnswerFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAnswerFile(e.target.files[0]);
    }
  };

  const handleSolutionFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSolutionFile(e.target.files[0]);
    }
  };

  const handleSubmit = () => {
    onStartIngestion({
      uploadedFile,
      questionFile,
      answerFile,
      solutionFile
    });
  };

  return (
    <>
      <Card className="mb-4">
        {/* File Type Selection */}
        <Row className="mb-4">
          <Col md={6}>
            <div
              className={`file-type-card ${fileType === 'combined' ? 'selected' : ''}`}
              onClick={() => setFileType('combined')}
            >
              <div className="file-type-radio">
                <div className={`radio-outer ${fileType === 'combined' ? 'selected' : ''}`}>
                  {fileType === 'combined' && <div className="radio-inner"></div>}
                </div>
              </div>
              <div className="file-type-content">
                <h6>Combined File (all content in one document)</h6>
                <p>Single document with questions, answer key & solutions together</p>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div
              className={`file-type-card ${fileType === 'separate' ? 'selected' : ''}`}
              onClick={() => setFileType('separate')}
            >
              <div className="file-type-radio">
                <div className={`radio-outer ${fileType === 'separate' ? 'selected' : ''}`}>
                  {fileType === 'separate' && <div className="radio-inner"></div>}
                </div>
              </div>
              <div className="file-type-content">
                <h6>Separate Files (Question / Answer / Solution)</h6>
                <p>Upload three separate documents for question text, answer key & solutions</p>
              </div>
            </div>
          </Col>
        </Row>

        {/* Target Selection */}
        <Row className="mb-4">
          <Col md={6}>
            <Form.Label>Target :</Form.Label>
            <Select
              value={selectedTarget}
              onChange={(e) => setSelectedTarget(e.target.value)}
            >
              <option value="">Select Target</option>

              {targets?.map((target) => (
                <option key={target.target_id} value={target.target_id}>
                  {target.aname}
                </option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* File Upload Area */}
        {fileType === 'combined' ? (
          <div
            className={`upload-area ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('fileInput').click()}
          >
            <input
              type="file"
              id="fileInput"
              accept=".pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <div className="upload-icon">
              <Upload size={32} />
            </div>
            <h5>Upload Combined Question File</h5>
            <p>Drag & drop files here, or click to browse</p>
            <p className="upload-formats">Supports: PDF Only</p>
            {uploadedFile && (
              <div className="uploaded-file-info">
                <p className="file-name">📄 {uploadedFile.name}</p>
              </div>
            )}
          </div>
        ) : (
          <Row>
            <Col md={4}>
              <div
                className="upload-area-small"
                onClick={() => document.getElementById('questionFileInput').click()}
              >
                <input
                  type="file"
                  id="questionFileInput"
                  accept=".pdf"
                  onChange={handleQuestionFileChange}
                  style={{ display: 'none' }}
                />
                <div className="upload-icon-small">
                  <Upload size={24} />
                </div>
                <h6>Question File</h6>
                <p className="upload-formats">PDF Only</p>
                {questionFile && (
                  <div className="uploaded-file-info-small">
                    <p className="file-name-small">📄 {questionFile.name}</p>
                  </div>
                )}
              </div>
            </Col>
            <Col md={4}>
              <div
                className="upload-area-small"
                onClick={() => document.getElementById('answerFileInput').click()}
              >
                <input
                  type="file"
                  id="answerFileInput"
                  accept=".pdf"
                  onChange={handleAnswerFileChange}
                  style={{ display: 'none' }}
                />
                <div className="upload-icon-small">
                  <Upload size={24} />
                </div>
                <h6>Answer Key</h6>
                <p className="upload-formats">PDF Only</p>
                {answerFile && (
                  <div className="uploaded-file-info-small">
                    <p className="file-name-small">📄 {answerFile.name}</p>
                  </div>
                )}
              </div>
            </Col>
            <Col md={4}>
              <div
                className="upload-area-small"
                onClick={() => document.getElementById('solutionFileInput').click()}
              >
                <input
                  type="file"
                  id="solutionFileInput"
                  accept=".pdf"
                  onChange={handleSolutionFileChange}
                  style={{ display: 'none' }}
                />
                <div className="upload-icon-small">
                  <Upload size={24} />
                </div>
                <h6>Solution File</h6>
                <p className="upload-formats">PDF Only</p>
                {solutionFile && (
                  <div className="uploaded-file-info-small">
                    <p className="file-name-small">📄 {solutionFile.name}</p>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Card>

      <div className="text-center">
        <button
          className="btn sec-btn1 start-ingestion-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <Loader /> : 'Start Ingestion'}
        </button>
      </div>
    </>
  );
};

export default UploadForm;

